import requestAxios from '../../../util/requestAxios';
import configData from '../../../config';

function isLocalPortalHost() {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
}

/** Portal origin for SSO handoff links — local dev uses the page host; prod uses config. */
function resolvePortalOrigin() {
    if (isLocalPortalHost()) {
        return window.location.origin;
    }

    const fromConfig = String(configData.SERVER_URL_BASE || '').replace(/\/$/, '');
    if (fromConfig) return fromConfig;

    if (typeof window !== 'undefined') {
        return window.location.origin;
    }

    return '';
}

function normalizeSsoLink(raw) {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.replace(/^"|"$/g, '');
    return String(raw);
}

function rewriteLinkOrigin(link, origin) {
    if (!link || !origin) return link;
    try {
        const parsed = new URL(link);
        return `${origin.replace(/\/$/, '')}${parsed.pathname}${parsed.search}`;
    } catch {
        return link;
    }
}

function normalizeStreakCalendar(raw) {
    if (!raw) return null;
    const days = (raw.days || raw.Days || []).map((day) => ({
        date: day.date || day.Date,
        count: day.count ?? day.Count ?? 0,
        qualifies: day.qualifies ?? day.Qualifies ?? false,
    }));
    return {
        oolaUserId: raw.oolaUserId ?? raw.OolaUserId,
        exigoUserId: raw.exigoUserId ?? raw.ExigoUserId,
        timeZoneId: raw.timeZoneId || raw.TimeZoneId || 'UTC',
        currentStreak: raw.currentStreak ?? raw.CurrentStreak ?? 0,
        longestStreak: raw.longestStreak ?? raw.LongestStreak ?? 0,
        days,
    };
}

function normalizeRepairResult(raw) {
    if (!raw) return null;
    return {
        actionsAdded: raw.actionsAdded ?? raw.ActionsAdded ?? 0,
        repairedDates: raw.repairedDates || raw.RepairedDates || [],
        alreadyCompleteDates:
            raw.alreadyCompleteDates || raw.AlreadyCompleteDates || [],
        currentStreak: raw.currentStreak ?? raw.CurrentStreak ?? 0,
        longestStreak: raw.longestStreak ?? raw.LongestStreak ?? 0,
    };
}

export async function fetchSsoLinks(exigoCustomerId) {
    const id = Number(String(exigoCustomerId || '').replace(/\D/g, ''));
    if (!id) {
        return { ok: false, error: 'Enter a valid customer #' };
    }

    const { error, data, status } = await requestAxios({
        url: `Login/GetSSODashboardLinkByCustomerId/${id}`,
        method: 'post',
    });

    if (error) {
        const msg =
            error.response?.data?.message ||
            error.response?.data ||
            error.message ||
            'Could not generate SSO link.';
        return { ok: false, error: String(msg), status };
    }

    let v1Link = normalizeSsoLink(data);
    const origin = resolvePortalOrigin();
    if (origin) {
        v1Link = rewriteLinkOrigin(v1Link, origin);
    }

    const v2Link = v1Link.includes('?')
        ? `${v1Link}&portalV2=1`
        : `${v1Link}?portalV2=1`;

    return { ok: true, v1Link, v2Link, exigoCustomerId: id };
}

export async function fetchStreakCalendar({
    exigoCustomerId,
    startDate,
    endDate,
    token,
}) {
    const id = Number(String(exigoCustomerId || '').replace(/\D/g, ''));
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    return requestAxios({
        url: `AdminLab/StreakCalendar/${id}?${params.toString()}`,
        method: 'get',
        token,
    }).then((result) => ({
        ...result,
        data: normalizeStreakCalendar(result.data),
    }));
}

export async function repairStreakDays({ exigoCustomerId, dates, token }) {
    const id = Number(String(exigoCustomerId || '').replace(/\D/g, ''));
    return requestAxios({
        url: 'AdminLab/RepairStreakDays',
        method: 'post',
        token,
        data: {
            exigoUserId: id,
            dates,
        },
    }).then((result) => ({
        ...result,
        data: normalizeRepairResult(result.data),
    }));
}
